import { useApp } from '@/context/AppContext'
import { useFilteredBreakValues } from '@/hooks/useFilteredBreakValues'
import { HorizontalStackedBar } from '@/components/charts/HorizontalStackedBar'
import { buildFilterKey } from '@/services/filterEngine'
import { getResponseColor } from '@/utils/colors'

// Helper to get threat color
const getThreatColor = (label: string) => getResponseColor('threat', label)

// Map question IDs to full Tableau labels (verified against topline report Q1, pages 12-13)
const THREAT_LABELS: Record<string, string> = {
  'Threat_US': 'The United States',
  'Threat_OutsideInterests': 'Outside interests controlling our economy',
  'Threat_Billionaires': 'Billionaires and oligarchs',
  'Threat_Crime': 'Crime and lawlessness',
  'Threat_Racism': 'Racism',
  'Threat_MAGA': 'MAGA-supporters',
  'Threat_Socialism': 'Socialism',
  'Threat_Environmentalists': 'Environmentalists',
  'Threat_ClimateChange': 'Climate change',
  'Threat_UN': 'The United Nations',
  'Threat_LossFaith': 'Loss of faith in God',
  'Threat_Immigration': 'Immigration',
  'Threat_Overspending': 'Overspending and taxation',
  'Threat_Misinformation': 'Misinformation/Fake News',
  'Threat_Divisions': 'Growing divisions in Canadian society',
  'Threat_Dependence': 'Dependence on other nations for food, security, and energy',
  'Threat_Corporations': 'Corporations and the rich',
  'Threat_SocialDisorder': 'Social disorder',
  'Threat_WesternSeparatists': 'Western Separatists',
}

// Response order: most threatening to least, with Unsure before Not a threat
const THREAT_RESPONSE_ORDER = ['A top threat', 'Major threat', 'Minor threat', 'Unsure', 'Not a threat']

// Sort responses according to threat order
const sortThreatResponses = (responses: Array<{ label: string; percentage: number }>) => {
  return [...responses].sort((a, b) => {
    const aIdx = THREAT_RESPONSE_ORDER.indexOf(a.label)
    const bIdx = THREAT_RESPONSE_ORDER.indexOf(b.label)
    if (aIdx === -1) return 1
    if (bIdx === -1) return -1
    return aIdx - bIdx
  })
}

export function FutureThreats() {
  const { state } = useApp()
  const { data, filters } = state
  const { selectedBreak, selectedProvince } = filters

  const breakDef = data?.breaks.find(b => b.id === selectedBreak)
  const { breakValues: filteredBreakValues } = useFilteredBreakValues()
  const breakValues = selectedBreak !== 'total' && breakDef ? filteredBreakValues : ['total']
  const isBreakActive = selectedBreak !== 'total'
  const breakName = breakDef?.name || 'Total'
  const displayMap = breakDef?.displayMap || {}
  const getDisplayName = (value: string) => displayMap[value] || value

  // Get threat questions, sorted by highest threat level (A top threat + Major threat)
  const threatQuestions = data
    ? Object.entries(data.questions)
        .filter(([id]) => id.startsWith('Threat_'))
        .map(([id, question]) => {
          // Calculate threat score for sorting (sum of top + major threat percentages)
          const filterKey = buildFilterKey('total', 'total')
          const questionData = question.data[filterKey]
          const topThreat = questionData?.responses.find(r => r.label === 'A top threat')?.percentage || 0
          const majorThreat = questionData?.responses.find(r => r.label === 'Major threat')?.percentage || 0
          return { id, question, threatScore: topThreat + majorThreat }
        })
        .sort((a, b) => b.threatScore - a.threatScore) // Sort by highest threat first
        .map(({ id, question }) => [id, question] as const)
    : []

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-800 dark:text-white">
          Future Threats to Canada
        </h1>
        <span className="text-sm text-slate-500 dark:text-slate-400">
          Break: {breakName}
        </span>
      </div>

      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm p-6">
        <p className="text-slate-600 dark:text-slate-300 mb-4">
          When it comes to the future of our country, how much of a threat are each of the following?
        </p>

        {/* Legend */}
        <div className="flex flex-wrap gap-4 mb-6 pb-4 border-b border-slate-100 dark:border-slate-700">
          {THREAT_RESPONSE_ORDER.map(label => (
            <div key={label} className="flex items-center gap-2">
              <div
                className="w-4 h-4 rounded"
                style={{ backgroundColor: getThreatColor(label) }}
              />
              <span className="text-xs text-slate-600 dark:text-slate-400">{label}</span>
            </div>
          ))}
        </div>

        <div className="space-y-6">
          {!data ? (
            <p className="text-slate-500 dark:text-slate-400">Loading threat questions...</p>
          ) : isBreakActive ? (
            // Break is active - show multiple rows per question
            threatQuestions.map(([questionId, question]) => {
              const rows = breakValues.map(value => {
                const filterKey = buildFilterKey('total', selectedBreak, value, selectedProvince)
                let questionData = question.data[filterKey]
                if (!questionData && selectedProvince) {
                  questionData = question.data[buildFilterKey('total', selectedBreak, value)]
                }
                return { value, filterKey, data: questionData }
              }).filter(row => row.data)

              if (rows.length === 0) return null

              const questionLabel = THREAT_LABELS[questionId] || question.shortText || questionId.replace('Threat_', '').replace(/_/g, ' ')

              return (
                <div key={questionId} className="border-b border-slate-100 dark:border-slate-700 pb-4 last:border-0 last:pb-0">
                  <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">{questionLabel}</h3>
                  <div className="space-y-2">
                    {rows.map(row => (
                      <div key={row.filterKey} className="flex items-center gap-2">
                        <div className="w-32 text-xs text-slate-500 dark:text-slate-400 text-right shrink-0" title={getDisplayName(row.value)}>
                          {getDisplayName(row.value)}
                        </div>
                        <div className="flex-1">
                          <HorizontalStackedBar
                            segments={sortThreatResponses(row.data!.responses)}
                            scaleType="threat"
                            height={24}
                            showLabels={false}
                            normalize={true}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )
            })
          ) : (
            // Total (no break) - show single bar per question with full labels
            threatQuestions.map(([questionId, question]) => {
              const filterKey = buildFilterKey('total', 'total')
              const questionData = question.data[filterKey]

              if (!questionData) return null

              const questionLabel = THREAT_LABELS[questionId] || question.shortText || questionId.replace('Threat_', '').replace(/_/g, ' ')

              return (
                <div key={questionId} className="border-b border-slate-100 dark:border-slate-700 pb-4 last:border-0">
                  <HorizontalStackedBar
                    segments={sortThreatResponses(questionData.responses)}
                    scaleType="threat"
                    questionText={questionLabel}
                    sampleSize={questionData.sampleSize}
                    height={32}
                    showLabels={false}
                  />
                </div>
              )
            })
          )}
        </div>
      </div>
    </div>
  )
}
